import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '@/hooks/useMobile';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContext = {
    state: 'expanded' | 'collapsed';
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider.');
    }
    return context;
}

const SidebarProvider = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        defaultOpen?: boolean;
        open?: boolean;
        onOpenChange?: (open: boolean) => void;
    }
>(({ defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children, ...props }, ref) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);

    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
        (value: boolean | ((value: boolean) => boolean)) => {
            const openState = typeof value === 'function' ? value(open) : value;
            if (setOpenProp) {
                setOpenProp(openState);
            } else {
                _setOpen(openState);
            }
            document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        },
        [setOpenProp, open]
    );

    const toggleSidebar = React.useCallback(() => {
        return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleSidebar();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    const state = open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContext>(
        () => ({
            state,
            open,
            setOpen,
            isMobile,
            openMobile,
            setOpenMobile,
            toggleSidebar
        }),
        [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
    );

    return (
        <SidebarContext.Provider value={contextValue}>
            <TooltipProvider delayDuration={0}>
                <div
                    style={
                        {
                            '--sidebar-width': SIDEBAR_WIDTH,
                            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                            ...style
                        } as React.CSSProperties
                    }
                    className={cn(
                        'group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar',
                        className
                    )}
                    ref={ref}
                    {...props}
                >
                    {children}
                </div>
            </TooltipProvider>
        </SidebarContext.Provider>
    );
});
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        side?: 'left' | 'right';
        variant?: 'sidebar' | 'floating' | 'inset';
        collapsible?: 'offcanvas' | 'icon' | 'none';
    }
>(({ side = 'left', variant = 'sidebar', collapsible = 'offcanvas', className, children, ...props }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === 'none') {
        return (
            <div
                className={cn(
                    'flex h-full w-[var(--sidebar-width)] flex-col bg-sidebar text-sidebar-foreground',
                    className
                )}
                ref={ref}
                {...props}
            >
                {children}
            </div>
        );
    }

    if (isMobile) {
        return (
            <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
                <SheetContent
                    id='app-sidebar'
                    data-sidebar='sidebar'
                    data-mobile='true'
                    className='w-[var(--sidebar-width)] !border-r-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden'
                    style={
                        {
                            '--sidebar-width': SIDEBAR_WIDTH_MOBILE
                        } as React.CSSProperties
                    }
                    side={side}
                >
                    <div className='flex h-full w-full flex-col'>{children}</div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <div
            ref={ref}
            className='group peer hidden md:block text-sidebar-foreground'
            data-state={state}
            data-collapsible={state === 'collapsed' ? collapsible : ''}
            data-variant={variant}
            data-side={side}
        >
            <div
                className={cn(
                    'duration-200 relative h-svh w-[var(--sidebar-width)] bg-transparent transition-[width] ease-linear',
                    'group-data-[collapsible=offcanvas]:w-0',
                    'group-data-[side=right]:rotate-180',
                    variant === 'floating' || variant === 'inset'
                        ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_1rem)]'
                        : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]'
                )}
            />
            <div
                className={cn(
                    'duration-200 fixed inset-y-0 z-10 hidden h-svh w-[var(--sidebar-width)] transition-[left,right,width] ease-linear md:flex',
                    side === 'left'
                        ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
                        : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
                    variant === 'floating' || variant === 'inset'
                        ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_1rem_+2px)]'
                        : 'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
                    className
                )}
                {...props}
            >
                <div
                    data-sidebar='sidebar'
                    className='flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow'
                >
                    {children}
                </div>
            </div>
        </div>
    );
});
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = React.forwardRef<React.ComponentRef<typeof Button>, React.ComponentProps<typeof Button>>(
    ({ children, className, onClick, size = 'icon', ...props }, ref) => {
        const { t } = useTranslation();
        const { toggleSidebar, isMobile, open, openMobile } = useSidebar();
        const isOpen = isMobile ? openMobile : open;

        return (
            <Button
                ref={ref}
                type='button'
                data-sidebar='trigger'
                data-state={isOpen ? 'open' : 'closed'}
                aria-expanded={isOpen}
                aria-controls='app-sidebar'
                variant='ghost'
                size={size}
                className={cn(size === 'icon' && 'h-7 w-7', className)}
                onClick={(event) => {
                    onClick?.(event);
                    toggleSidebar();
                }}
                {...props}
            >
                <PanelLeft />
                {children}
                <span className='sr-only'>{t('common.toggleSidebar')}</span>
            </Button>
        );
    }
);
SidebarTrigger.displayName = 'SidebarTrigger';

const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'>>(
    ({ className, ...props }, ref) => {
        const { t } = useTranslation();
        const { toggleSidebar } = useSidebar();

        return (
            <button
                ref={ref}
                data-sidebar='rail'
                aria-label={t('common.toggleSidebar')}
                tabIndex={-1}
                onClick={toggleSidebar}
                title={t('common.toggleSidebar')}
                className={cn(
                    'absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
                    '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize',
                    '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
                    'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar',
                    '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
                    '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarRail.displayName = 'SidebarRail';

const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<'main'>>(({ className, ...props }, ref) => {
    return (
        <main
            ref={ref}
            className={cn(
                'relative flex min-h-svh flex-1 flex-col bg-background',
                'peer-data-[variant=inset]:min-h-[calc(100svh-1rem)] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow',
                className
            )}
            {...props}
        />
    );
});
SidebarInset.displayName = 'SidebarInset';

const SidebarInput = React.forwardRef<React.ComponentRef<typeof Input>, React.ComponentProps<typeof Input>>(
    ({ className, ...props }, ref) => {
        return (
            <Input
                ref={ref}
                data-sidebar='input'
                className={cn(
                    'h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarInput.displayName = 'SidebarInput';

const sidebarHeaderVariants = cva('flex flex-col gap-2 p-2', {
    variants: {
        variant: {
            default: '',
            appShell: 'gap-0 border-b border-sidebar-border px-3 py-3'
        }
    },
    defaultVariants: { variant: 'default' }
});

const SidebarHeader = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & VariantProps<typeof sidebarHeaderVariants>
>(({ className, variant, ...props }, ref) => {
    return (
        <div ref={ref} data-sidebar='header' className={cn(sidebarHeaderVariants({ variant }), className)} {...props} />
    );
});
SidebarHeader.displayName = 'SidebarHeader';

const sidebarFooterVariants = cva('flex flex-col gap-2 p-2', {
    variants: {
        variant: {
            default: '',
            appShell: 'gap-2 border-t border-sidebar-border p-3'
        }
    },
    defaultVariants: { variant: 'default' }
});

const SidebarFooter = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & VariantProps<typeof sidebarFooterVariants>
>(({ className, variant, ...props }, ref) => {
    return (
        <div ref={ref} data-sidebar='footer' className={cn(sidebarFooterVariants({ variant }), className)} {...props} />
    );
});
SidebarFooter.displayName = 'SidebarFooter';

const sidebarSeparatorVariants = cva('mx-2 w-auto bg-sidebar-border', {
    variants: {
        variant: {
            default: '',
            section: 'my-4 mx-0'
        }
    },
    defaultVariants: { variant: 'default' }
});

const SidebarSeparator = React.forwardRef<
    React.ComponentRef<typeof Separator>,
    React.ComponentProps<typeof Separator> & VariantProps<typeof sidebarSeparatorVariants>
>(({ className, variant, ...props }, ref) => {
    return (
        <Separator
            ref={ref}
            data-sidebar='separator'
            className={cn(sidebarSeparatorVariants({ variant }), className)}
            {...props}
        />
    );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const sidebarContentVariants = cva(
    'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
    {
        variants: {
            variant: {
                default: '',
                appShell: 'gap-0 px-3 py-4'
            }
        },
        defaultVariants: { variant: 'default' }
    }
);

const SidebarContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & VariantProps<typeof sidebarContentVariants>
>(({ className, variant, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar='content'
            className={cn(sidebarContentVariants({ variant }), className)}
            {...props}
        />
    );
});
SidebarContent.displayName = 'SidebarContent';

const sidebarGroupVariants = cva('relative flex w-full min-w-0 flex-col p-2', {
    variants: {
        variant: {
            default: '',
            flush: 'p-0'
        }
    },
    defaultVariants: { variant: 'default' }
});

const SidebarGroup = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & VariantProps<typeof sidebarGroupVariants>
>(({ className, variant, ...props }, ref) => {
    return (
        <div ref={ref} data-sidebar='group' className={cn(sidebarGroupVariants({ variant }), className)} {...props} />
    );
});
SidebarGroup.displayName = 'SidebarGroup';

const sidebarGroupLabelVariants = cva(
    'duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
    {
        variants: {
            variant: {
                default: '',
                eyebrow:
                    'h-auto px-2.5 pb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-sidebar-foreground/40'
            }
        },
        defaultVariants: { variant: 'default' }
    }
);

const SidebarGroupLabel = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & { asChild?: boolean } & VariantProps<typeof sidebarGroupLabelVariants>
>(({ className, asChild = false, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
        <Comp
            ref={ref}
            data-sidebar='group-label'
            className={cn(
                sidebarGroupLabelVariants({ variant }),
                'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
                className
            )}
            {...props}
        />
    );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'> & { asChild?: boolean }>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';

        return (
            <Comp
                ref={ref}
                data-sidebar='group-action'
                className={cn(
                    'absolute right-3 top-3.5 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
                    'after:absolute after:-inset-2 after:md:hidden',
                    'group-data-[collapsible=icon]:hidden',
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} data-sidebar='group-content' className={cn('w-full text-sm', className)} {...props} />
    )
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

const sidebarMenuVariants = cva('flex w-full min-w-0 flex-col gap-1', {
    variants: {
        variant: {
            default: '',
            compact: 'gap-0.5'
        }
    },
    defaultVariants: { variant: 'default' }
});

const SidebarMenu = React.forwardRef<
    HTMLUListElement,
    React.ComponentProps<'ul'> & VariantProps<typeof sidebarMenuVariants>
>(({ className, variant, ...props }, ref) => (
    <ul ref={ref} data-sidebar='menu' className={cn(sidebarMenuVariants({ variant }), className)} {...props} />
));
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
    <li ref={ref} data-sidebar='menu-item' className={cn('group/menu-item relative', className)} {...props} />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
    'peer/menu-button flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 disabled:cursor-default group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-foreground/10 data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                outline:
                    'border border-sidebar-border bg-sidebar hover:border-sidebar-primary hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                brand: 'h-11 rounded-lg px-2 text-sidebar-foreground hover:bg-transparent hover:text-sidebar-foreground [&>a]:justify-start',
                organization:
                    'rounded-xl border border-sidebar-border bg-sidebar-accent px-3 py-2.5 text-sidebar-foreground hover:!bg-sidebar-foreground/10 hover:text-sidebar-foreground data-[state=open]:!bg-sidebar-foreground/10',
                navigation:
                    'h-auto rounded-lg px-2.5 py-2 text-sm text-sidebar-foreground/60 hover:!bg-sidebar-accent hover:!text-sidebar-foreground data-[active=true]:!bg-sidebar-foreground/10 data-[active=true]:!text-sidebar-foreground [&>a]:gap-2.5',
                profile: 'h-auto rounded-xl px-2.5 py-2 data-[state=open]:!bg-sidebar-foreground/10'
            },
            size: {
                default: 'h-8 text-sm',
                sm: 'h-7 text-xs',
                lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
                brand: 'h-11 text-sm',
                navigation: 'h-auto text-sm',
                shell: 'h-auto text-sm group-data-[collapsible=icon]:!p-0'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const SidebarMenuButton = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> & {
        asChild?: boolean;
        isActive?: boolean;
        tooltip?: string | React.ComponentProps<typeof TooltipContent>;
    } & VariantProps<typeof sidebarMenuButtonVariants>
>(({ asChild = false, isActive = false, variant = 'default', size = 'default', tooltip, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();

    const button = (
        <Comp
            ref={ref}
            data-sidebar='menu-button'
            data-size={size}
            data-active={isActive}
            className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
            {...props}
        />
    );

    if (!tooltip) {
        return button;
    }

    if (typeof tooltip === 'string') {
        tooltip = { children: tooltip };
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side='right' align='center' hidden={state !== 'collapsed' || isMobile} {...tooltip} />
        </Tooltip>
    );
});
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuAction = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> & {
        asChild?: boolean;
        showOnHover?: boolean;
    }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            ref={ref}
            data-sidebar='menu-action'
            className={cn(
                'absolute right-1 top-1.5 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
                'after:absolute after:-inset-2 after:md:hidden',
                'group-data-[collapsible=icon]:hidden',
                showOnHover &&
                    'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
                className
            )}
            {...props}
        />
    );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-sidebar='menu-badge'
            className={cn(
                'absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none',
                'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
                'group-data-[collapsible=icon]:hidden',
                className
            )}
            {...props}
        />
    )
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        showIcon?: boolean;
    }
>(({ className, showIcon = false, ...props }, ref) => {
    const width = React.useMemo(() => {
        return `${Math.floor(Math.random() * 40) + 50}%`;
    }, []);

    return (
        <div
            ref={ref}
            data-sidebar='menu-skeleton'
            className={cn('rounded-md h-8 flex gap-2 px-2 items-center', className)}
            {...props}
        >
            {showIcon && <Skeleton className='size-4 rounded-md' data-sidebar='menu-skeleton-icon' />}
            <Skeleton
                className='h-4 flex-1 max-w-[--skeleton-width]'
                data-sidebar='menu-skeleton-text'
                style={{ '--skeleton-width': width } as React.CSSProperties}
            />
        </div>
    );
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const sidebarMenuSubVariants = cva(
    'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden',
    {
        variants: {
            variant: {
                default: '',
                navigation: 'ml-6 mt-1 translate-x-0 gap-0.5 border-l-0 px-0 py-0'
            }
        },
        defaultVariants: { variant: 'default' }
    }
);

const SidebarMenuSub = React.forwardRef<
    HTMLUListElement,
    React.ComponentProps<'ul'> & VariantProps<typeof sidebarMenuSubVariants>
>(({ className, variant, ...props }, ref) => (
    <ul ref={ref} data-sidebar='menu-sub' className={cn(sidebarMenuSubVariants({ variant }), className)} {...props} />
));
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ ...props }, ref) => (
    <li ref={ref} {...props} />
));
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const sidebarMenuSubButtonVariants = cva(
    'flex h-7 min-w-0 -translate-x-px cursor-pointer items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 disabled:cursor-default aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground group-data-[collapsible=icon]:hidden',
    {
        variants: {
            variant: {
                default: '',
                navigation:
                    'h-auto translate-x-0 rounded-lg px-2.5 py-1.5 text-[13px] text-sidebar-foreground/50 hover:!bg-sidebar-accent hover:!text-sidebar-foreground data-[active=true]:!bg-sidebar-foreground/10 data-[active=true]:!text-sidebar-foreground [&>a]:gap-2.5 [&>svg]:size-3.5 [&>svg]:text-sidebar-foreground/50'
            }
        },
        defaultVariants: { variant: 'default' }
    }
);

const SidebarMenuSubButton = React.forwardRef<
    HTMLAnchorElement,
    React.ComponentProps<'a'> & {
        asChild?: boolean;
        size?: 'sm' | 'md';
        isActive?: boolean;
    } & VariantProps<typeof sidebarMenuSubButtonVariants>
>(({ asChild = false, size = 'md', isActive, variant, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';

    return (
        <Comp
            ref={ref}
            data-sidebar='menu-sub-button'
            data-size={size}
            data-active={isActive}
            className={cn(
                sidebarMenuSubButtonVariants({ variant }),
                'data-[active=true]:bg-sidebar-foreground/10 data-[active=true]:text-sidebar-foreground data-[active=true]:[&>svg]:text-sidebar-foreground',
                size === 'sm' && 'text-xs',
                size === 'md' && 'text-sm',
                className
            )}
            {...props}
        />
    );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupAction,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInput,
    SidebarInset,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar
};
